import { useMemo } from "react";
import { useParams } from "react-router";
import {
  useGetClusterLabelNamesApiV1ExploreDatasetIdClusterClusterIdLabelsGet,
  useGetDatasetLabelNamesApiV1ExploreDatasetIdLabelsGet,
} from "vl-api/vlComponents";
import { useGetExplorationQueryParams } from "hooks/useGetExplorationQueryParams";

export interface LabelItem {
  id: string;
  text: string;
  selected: boolean;
}

export const useLabelsData = () => {
  const { datasetId, clusterId } = useParams();

  const explorationQueryParams = useGetExplorationQueryParams();

  const clusterLabelsQuery =
    useGetClusterLabelNamesApiV1ExploreDatasetIdClusterClusterIdLabelsGet(
      {
        pathParams: { datasetId: datasetId!, clusterId: clusterId! },
        queryParams: explorationQueryParams,
      },
      {
        refetchOnMount: false,
        enabled: !!clusterId,
      },
    );

  const datasetLabelsQuery =
    useGetDatasetLabelNamesApiV1ExploreDatasetIdLabelsGet(
      {
        pathParams: { datasetId: datasetId! },
        queryParams: explorationQueryParams,
      },
      {
        refetchOnMount: false,
        enabled: !clusterId,
      },
    );

  const data = clusterId ? clusterLabelsQuery.data : datasetLabelsQuery.data;
  const isLoading = clusterId
    ? clusterLabelsQuery.isLoading
    : datasetLabelsQuery.isLoading;

  const labels = useMemo((): LabelItem[] => {
    if (!data?.labels || !Array.isArray(data.labels)) {
      return [];
    }

    const isUnlabeledRow = (item: any) =>
      String(item?.label ?? "")
        .trim()
        .toLowerCase() === "unlabeled";

    const sorted = [...data.labels].sort((a: any, b: any) => {
      const aUn = isUnlabeledRow(a);
      const bUn = isUnlabeledRow(b);
      if (aUn !== bUn) {
        return aUn ? -1 : 1;
      }
      return String(a.label ?? "").localeCompare(
        String(b.label ?? ""),
        undefined,
        {
          sensitivity: "base",
        },
      );
    });

    return sorted.map((label: any, index: number) => ({
      id: String(index),
      text: label.label || "",
      source: label.source,
      selected: false,
    }));
  }, [data]);

  return {
    isLoading,
    labels,
  };
};
